import React, { useState, useEffect } from "react";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { abTestApi, agentApi } from "../../api/client";
import { Button, Badge, Modal } from "../../components/ui";

const METRIC_LABELS = { resolution_rate: "Resolution Rate", csat: "CSAT Score", latency: "Response Latency", escalation_rate: "Escalation Rate" };
const STATUS_COLORS = { draft: COLORS.textMuted, running: COLORS.green, completed: COLORS.accent, stopped: COLORS.pink };

// FCOV: pure helpers extracted for unit testing; wired back into the component below.
// Sum of variant weights (variants must total 100% before a test can be created).
export function totalVariantWeight(variants) {
  return (variants || []).reduce((s, v) => s + (Number(v?.weight) || 0), 0);
}
// Resolve a display name for an agent id from the loaded agents list, with
// short-id and em-dash fallbacks.
export function agentNameFor(agents, id) {
  return (agents || []).find(a => a.id === id)?.name || id?.slice(0, 8) || "—";
}
// A test is creatable only when it has a name, every variant has an agent, and
// the weights total exactly 100%.
export function canCreateTest(form) {
  if (!form || !form.name || !form.name.trim()) return false;
  if ((form.variants || []).some(v => !v.agent_id)) return false;
  return totalVariantWeight(form.variants) === 100;
}

const ABTestPanel = () => {
  const [tests, setTests] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);

  const [form, setForm] = useState({
    name: "", channel: "chat", primary_metric: "resolution_rate",
    min_conversations: 50, auto_promote: false,
    variants: [{ agent_id: "", weight: 50, label: "Control" }, { agent_id: "", weight: 50, label: "Variant B" }],
  });

  const fetchTests = async () => {
    setLoading(true);
    try {
      const res = await abTestApi.list();
      if (res.success) setTests(res.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    fetchTests();
    agentApi.list().then(r => { if (r.success) setAgents(r.data || []); }).catch(() => {});
  }, []);

  const handleCreate = async () => {
    try {
      const res = await abTestApi.create(form);
      if (res.success) { setShowCreate(false); fetchTests(); }
    } catch {}
  };

  const handleStart = async (id) => {
    await abTestApi.start(id);
    fetchTests();
  };

  const handleStop = async (id) => {
    await abTestApi.stop(id);
    fetchTests();
  };

  const openDetail = async (id) => {
    setShowDetail(id);
    try {
      const res = await abTestApi.get(id);
      if (res.success) setDetailData(res.data);
    } catch {}
  };

  const agentName = (id) => agentNameFor(agents, id);

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>A/B Tests</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>Compare agent variants with real traffic</div>
        </div>
        <Button onClick={() => setShowCreate(true)}
          style={{ background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`, border: "none", color: "#fff", fontSize: 12 }}>
          + New Test
        </Button>
      </div>

      {loading ? (
        <div style={{ padding: 20, textAlign: "center", color: COLORS.textDim }}>Loading...</div>
      ) : tests.length === 0 ? (
        <div style={{
          padding: 30, textAlign: "center", borderRadius: 12,
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>{"\u2696"}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMuted }}>No A/B tests yet</div>
          <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>Split traffic between agents and measure which performs better</div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {tests.map(t => (
            <div key={t.id} onClick={() => openDetail(t.id)} style={{
              padding: 16, borderRadius: RADIUS.inner, background: COLORS.surface, boxShadow: MATERIALS.spatialShadow(),
              border: `1px solid ${(STATUS_COLORS[t.status] || COLORS.border)}25`,
              cursor: "pointer", transition: "border-color 0.15s",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{"\u2696"}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{t.name}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim }}>{t.channel} {"\u00B7"} {METRIC_LABELS[t.primary_metric]} {"\u00B7"} {(t.variants || []).length} variants</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {t.winner_agent_id && <Badge color={COLORS.green}>Winner: {agentName(t.winner_agent_id)}</Badge>}
                  <Badge color={STATUS_COLORS[t.status] || COLORS.textMuted}>{t.status}</Badge>
                </div>
              </div>
              {/* Variant bars */}
              <div style={{ display: "flex", gap: 4, height: 6, borderRadius: 3, overflow: "hidden" }}>
                {(t.variants || []).map((v, i) => (
                  <div key={i} style={{
                    flex: v.weight, background: i === 0 ? COLORS.accent : i === 1 ? COLORS.cyan : COLORS.orange,
                    borderRadius: 3,
                  }} />
                ))}
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 6, fontSize: 10, color: COLORS.textDim }}>
                {(t.variants || []).map((v, i) => (
                  <span key={i}>{v.label || agentName(v.agent_id)}: {v.weight}%</span>
                ))}
              </div>
              {/* Actions */}
              {t.status === "draft" && (
                <div style={{ marginTop: 8 }}>
                  <Button size="sm" onClick={e => { e.stopPropagation(); handleStart(t.id); }}
                    style={{ background: `${COLORS.green}15`, color: COLORS.green, border: `1px solid ${COLORS.green}30` }}>
                    Start Test
                  </Button>
                </div>
              )}
              {t.status === "running" && (
                <div style={{ marginTop: 8 }}>
                  <Button size="sm" onClick={e => { e.stopPropagation(); handleStop(t.id); }}
                    style={{ color: COLORS.pink }}>
                    Stop
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal open={showCreate} title="New A/B Test" onClose={() => setShowCreate(false)}>
        <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Test Name</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. GPT-4o vs Claude Sonnet"
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.inner, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 13 }} />
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Channel</label>
              <select value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.inner, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }}>
                {/* obs-completion F9: 'voice' removed — A/B metrics are recorded on the
                    orchestrator path (chat/whatsapp/sms/email); the native voice session
                    bypasses it, so a voice A/B test would collect zero metrics. */}
                {["chat", "whatsapp", "sms", "email"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Primary Metric</label>
              <select value={form.primary_metric} onChange={e => setForm(f => ({ ...f, primary_metric: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.inner, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }}>
                {Object.entries(METRIC_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* Variants */}
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 8 }}>Variants</div>
          {form.variants.map((v, i) => (
            <div key={i} style={{
              display: "flex", gap: 8, alignItems: "center", marginBottom: 8,
              padding: 10, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            }}>
              <input type="text" value={v.label} onChange={e => {
                const vs = [...form.variants]; vs[i] = { ...vs[i], label: e.target.value }; setForm(f => ({ ...f, variants: vs }));
              }} placeholder="Label" style={{ width: 80, padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 11 }} />
              <select value={v.agent_id} onChange={e => {
                const vs = [...form.variants]; vs[i] = { ...vs[i], agent_id: e.target.value }; setForm(f => ({ ...f, variants: vs }));
              }} style={{ flex: 1, padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 11 }}>
                <option value="">Select agent...</option>
                {agents
                  .filter(a => a.status !== "archived")
                  .map(a => {
                    const s = a.status || "live";
                    const isLive = s === "live" || s === "active";
                    return <option key={a.id} value={a.id}>{a.name}{!isLive ? ` (${s})` : ""}</option>;
                  })}
              </select>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input type="number" value={v.weight} onChange={e => {
                  const vs = [...form.variants]; vs[i] = { ...vs[i], weight: parseInt(e.target.value) || 0 }; setForm(f => ({ ...f, variants: vs }));
                }} min={1} max={99} style={{ width: 50, padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 11, textAlign: "center" }} />
                <span style={{ fontSize: 10, color: COLORS.textDim }}>%</span>
              </div>
              {form.variants.length > 2 && (
                <button onClick={() => setForm(f => ({ ...f, variants: f.variants.filter((_, j) => j !== i) }))}
                  style={{ background: "none", border: "none", color: COLORS.pink, cursor: "pointer", fontSize: 14 }}>{"\u2715"}</button>
              )}
            </div>
          ))}
          {form.variants.length < 5 && (
            <button onClick={() => setForm(f => ({
              ...f, variants: [...f.variants, { agent_id: "", weight: Math.max(1, 100 - totalVariantWeight(f.variants)), label: `Variant ${String.fromCharCode(65 + f.variants.length)}` }],
            }))} style={{ background: "none", border: `1px dashed ${COLORS.border}`, borderRadius: 8, padding: "8px 12px", width: "100%", color: COLORS.textMuted, fontSize: 11, cursor: "pointer", marginBottom: 14 }}>
              + Add Variant
            </button>
          )}
          <div style={{ fontSize: 10, color: totalVariantWeight(form.variants) === 100 ? COLORS.green : COLORS.pink, marginBottom: 14 }}>
            Total weight: {totalVariantWeight(form.variants)}% {totalVariantWeight(form.variants) !== 100 ? "(must be 100%)" : "\u2714"}
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Min Conversations</label>
              <input type="number" value={form.min_conversations} onChange={e => setForm(f => ({ ...f, min_conversations: parseInt(e.target.value) || 50 }))}
                min={10} max={10000}
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.inner, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "flex-end", paddingBottom: 4 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={form.auto_promote} onChange={e => setForm(f => ({ ...f, auto_promote: e.target.checked }))} />
                <span style={{ fontSize: 12, color: COLORS.text }}>Auto-promote winner</span>
              </label>
            </div>
          </div>

          <button onClick={handleCreate}
            disabled={!canCreateTest(form)}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
              background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
              color: "#fff", fontSize: 14, fontWeight: 600,
              cursor: "pointer", opacity: canCreateTest(form) ? 1 : 0.5,
            }}>
            Create A/B Test
          </button>
        </div>
      </Modal>

      {/* Detail Modal */}
      <Modal open={!!showDetail} title={detailData?.name || "A/B Test Results"} onClose={() => { setShowDetail(null); setDetailData(null); }}>
        {detailData ? (
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, fontSize: 11, color: COLORS.textMuted }}>
              <Badge color={STATUS_COLORS[detailData.status]}>{detailData.status}</Badge>
              <span>{detailData.channel}</span>
              <span>{"\u00B7"}</span>
              <span>Metric: {METRIC_LABELS[detailData.primary_metric]}</span>
              <span>{"\u00B7"}</span>
              <span>Min: {detailData.min_conversations} conversations</span>
            </div>

            {/* Variant results */}
            {(detailData.variants || []).map((v, i) => {
              const barColors = [COLORS.accent, COLORS.cyan, COLORS.orange, COLORS.pink, COLORS.green];
              return (
                <div key={i} style={{
                  padding: 14, borderRadius: 10, marginBottom: 10,
                  background: v.is_winner ? `${COLORS.green}08` : COLORS.bg,
                  border: `1px solid ${v.is_winner ? COLORS.green + "40" : COLORS.border}`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: barColors[i] }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{v.label || agentName(v.agent_id)}</span>
                      <span style={{ fontSize: 10, color: COLORS.textDim }}>({v.weight}%)</span>
                    </div>
                    {v.is_winner && <Badge color={COLORS.green}>Winner</Badge>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
                    {[
                      { label: "Conversations", value: v.conversations },
                      { label: "Resolution", value: `${v.resolution_rate}%`, color: COLORS.green },
                      { label: "Escalation", value: `${v.escalation_rate}%`, color: COLORS.pink },
                      { label: "CSAT", value: v.avg_csat != null ? `${v.avg_csat}/5` : "—" },
                      { label: "Avg Latency", value: v.avg_latency_ms != null ? `${v.avg_latency_ms}ms` : "—" },
                    ].map(s => (
                      <div key={s.label} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: COLORS.textDim }}>{s.label}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: s.color || COLORS.text }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ padding: 20, textAlign: "center", color: COLORS.textDim }}>Loading...</div>
        )}
      </Modal>
    </div>
  );
};

export default ABTestPanel;
